import React, { useEffect, useRef, useState } from 'react';
import FocusTrap from 'focus-trap-react';
import Button from '@mui/material/Button';
import { Backdrop, Box, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Paper from '@mui/material/Paper';
import { translate } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util';
import { FONT_GSS_FUTURA } from '@monorepo/shared/src/themes/defaultTheme';
import { isLastPage } from '@monorepo/shared/src/util/paging-util';
import { remCalc } from '@monorepo/shared/src/util/style-util';
import ChevronIcon from '@monorepo/shared/src/assets/icons/ChevronIcon';
import useBookContentStore from '@monorepo/shared/src/graph/state/query-hooks/use-book-content';
import { DEFAULT_FIRST_PAGE, PageTypes } from '@monorepo/shared/src/types';
import { Content } from './types';
import LetterTilesGrid from './components/LetterTilesGrid';
import PageNumberInput from './components/PageNumberInput';
import { disabled } from '@monorepo/shared/src/styles/sharedStyles';

const t = (key: string, options?) => translate('pageNavigation', key, options);

type Props = {
  activePages: Array<number | string | null>;
  bookId: number;
  hasPageNumberNavigation?: boolean;
  hasLetterNavigation?: boolean;
  onNavToPage: (pageNumber: number, isLastPage: boolean) => void;
  pageOffset: number;
  title?: string;
  totalPages: number;
  subtitle?: string;
};

const PageNavigation: React.FC<Props> = ({
  activePages,
  bookId,
  hasPageNumberNavigation = true,
  hasLetterNavigation = true,
  onNavToPage,
  pageOffset,
  title,
  totalPages,
  subtitle,
}) => {
  const backdropEl = useRef<HTMLDivElement>(null);
  const contentEl = useRef<HTMLDivElement>(null);
  const [navigationData, setNavigationData] = useState<{
    buttonLabel: string;
    pageNumber: string;
    selectedTile: Content | null;
  }>({
    buttonLabel: '',
    pageNumber: '',
    selectedTile: null,
  });
  const [showModal, setShowModal] = useState(false);
  const [{ content }] = useBookContentStore(bookId);
  const classes = styles();

  const handleGoButtonClick = () => {
    setNavigationData(() => ({
      ...navigationData,
      ...{ selectedTile: null },
    }));
    onNavToPage(
      Number(navigationData.pageNumber),
      isLastPage(
        parseInt(navigationData.pageNumber, 10),
        totalPages,
        DEFAULT_FIRST_PAGE[PageTypes.Pdf]
      )
    );
    handleToggleModal();
  };
  const handleMouseDown = (event: MouseEvent): void => {
    if (contentEl.current && !contentEl?.current?.contains(event.target as HTMLDivElement)) {
      handleToggleModal();
    }
  };
  const handleOnChange = (pageNumber: string) => {
    setNavigationData(() => ({
      ...navigationData,
      ...{ pageNumber },
    }));
  };
  const handleTileClick = (tile: Content) => {
    setNavigationData(() => ({
      ...navigationData,
      ...{ pageNumber: '', selectedTile: tile },
    }));
    onNavToPage(
      tile.pageNumber,
      isLastPage(tile.pageNumber, totalPages, DEFAULT_FIRST_PAGE[PageTypes.Pdf])
    );
    handleToggleModal();
  };
  const handleToggleModal = () => {
    setShowModal(() => !showModal);
  };

  useEffect(() => {
    if (backdropEl.current) {
      backdropEl.current.addEventListener('mousedown', handleMouseDown);
    }

    return () => {
      if (backdropEl.current) {
        backdropEl.current.removeEventListener('mousedown', handleMouseDown);
      }
    };
  }, [showModal]);

  useEffect(() => {
    // TODO: Handle cases where pageOffset > 1
    let buttonLabel;

    switch (true) {
      case !!(pageOffset && !activePages[0]):
        buttonLabel = `i of ${totalPages}`;
        break;
      case activePages.includes('cover'):
        buttonLabel = `cover of ${totalPages}`;
        break;
      case !!(activePages[0] && activePages[1]) && +activePages[1] <= totalPages:
        buttonLabel = `${activePages[0]} - ${activePages[1]} of ${totalPages}`;
        break;
      default:
        buttonLabel = `${activePages[0]} of ${totalPages}`;
    }

    setNavigationData(() => ({
      ...navigationData,
      ...{
        buttonLabel,
      },
    }));
  }, [activePages]);

  return (
    // TODO to convert to MUI popover
    // <FocusTrap active={showModal}>
    <Box>
      {showModal && (
        <>
          <Backdrop ref={backdropEl} className={classes.backdrop} open></Backdrop>
          <Box className={classes.contentContainer}>
            <Paper elevation={4}>
              <div ref={contentEl} className={classes.content}>
                <Typography variant={'h6'} className={classes.h6}>
                  {title || t('navigateToPageOrNumber')}
                </Typography>
                {hasLetterNavigation && (
                  <Typography variant={'subtitle1'}>{subtitle || t('typePageNumber')}</Typography>
                )}
                {hasPageNumberNavigation && (
                  <PageNumberInput
                    onChange={handleOnChange}
                    pageNumber={navigationData.pageNumber}
                    totalPages={totalPages}
                  />
                )}
                {hasPageNumberNavigation && (
                  <button
                    className={classes.goButtonContainer}
                    disabled={!navigationData.pageNumber}
                    onClick={handleGoButtonClick}
                  >
                    <Paper className={classes.goButton} elevation={0}>
                      {t('go')}
                    </Paper>
                  </button>
                )}
                {hasLetterNavigation && (
                  <LetterTilesGrid
                    onTileClick={handleTileClick}
                    selectedTile={navigationData.selectedTile}
                    tiles={content}
                  />
                )}
              </div>
            </Paper>
          </Box>
        </>
      )}
      <Box className={classes.buttonContainer}>
        <Button className={classes.toggleButton} disableTouchRipple onClick={handleToggleModal}>
          <span className={navigationData.buttonLabel && classes.label}>
            {navigationData.buttonLabel}
          </span>

          <Box className={classes.iconContainer}>
            <Box className={`${classes.icon} ${showModal && classes.rotate180}`}>
              <ChevronIcon />
            </Box>
          </Box>
        </Button>
      </Box>
    </Box>
    // </FocusTrap>
  );
};

const styles = makeStyles((theme: any) => ({
  backdrop: {
    zIndex: 8,
  },
  buttonContainer: {
    textAlign: 'center',
    position: 'relative',
    zIndex: 9,
  },
  content: {
    padding: remCalc(20),
  },
  contentContainer: {
    bottom: remCalc(20),
    left: `calc(50% - ${remCalc(258)})`,
    marginBottom: remCalc(35),
    position: 'absolute',
    width: remCalc(480),
    zIndex: 9,
  },
  goButtonContainer: {
    background: 'none',
    border: 'none',
    left: remCalc(14),
    position: 'relative',
    '&:disabled': disabled,
  },
  goButton: {
    backgroundColor: theme.palette.black.main,
    color: theme.palette.primary.contrastText,
    fontFamily: FONT_GSS_FUTURA,
    fontSize: remCalc(14),
    fontWeight: 'bolder',
    height: remCalc(38),
    padding: remCalc(10),
    textAlign: 'center',
    width: remCalc(42),
    '&:hover': {
      cursor: 'pointer',
    },
  },
  h6: {
    fontWeight: 'bold',
  },
  icon: {
    position: 'relative',
    top: '-3px',
    transform: 'rotate(180deg)',
    transition: 'transform 0.1s ease-in-out',
  },
  iconContainer: {
    display: 'inline-block',
    paddingBottom: remCalc(1),
    width: remCalc(15),
  },
  label: {
    display: 'inline-block',
    paddingRight: remCalc(10),
    color: 'black',
  },
  rotate180: {
    position: 'relative',
    top: '1px',
    transform: 'rotate(0deg)',
  },
  toggleButton: {
    backgroundColor: theme.palette.primary.contrastText,
    border: '2px solid black',
    fontFamily: FONT_GSS_FUTURA,
    fontWeight: 'bold',
    height: remCalc(30),
    minWidth: remCalc(100),
    '&:hover': {
      backgroundColor: theme.palette.primary.contrastText,
      cursor: 'pointer',
    },
  },
}));

export default PageNavigation;
